import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    FloppyDisk,
    Plus,
    Trash,
    Wallet as WalletIcon,
    Handshake,
    Users,
    ChartLineUp,
    CheckCircle,
    Warning,
    Lock,
    LockOpen,
    ShieldCheck,
    SkipForward,
    Scales,
} from "@phosphor-icons/react";
import MoneyInput from "../components/MoneyInput";

/**
 * Apertura saldi — one-time migration page for customers switching from paper/excel.
 *
 * Four sections, each editing current DB state directly (no transactions created):
 *  1. Fondi iniziali (wallets cash/bank balance)
 *  2. Residui fornitori (derives outstanding_credit = fido_limit - residuo)
 *  3. Sospesi clienti aperti (new clients with outstanding_debt pre-valued)
 *  4. Utili già accantonati (utili_accantonati wallet balance)
 *
 * A quadrabilità banner at top summarises the current balance equation.
 */
export default function SettingsApertura() {
    const { agencyId } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isOnboardingFlow = searchParams.get("onboarding") === "1";
    const [wallets, setWallets] = useState([]);
    const [providers, setProviders] = useState([]);
    const [clients, setClients] = useState([]);
    const [formula, setFormula] = useState(null);
    const [agency, setAgency] = useState(null);
    const [loading, setLoading] = useState(true);
    const [passwordModal, setPasswordModal] = useState(null); // 'lock' | 'unlock' | null

    const [walletEdits, setWalletEdits] = useState({}); // id -> string
    const [providerEdits, setProviderEdits] = useState({}); // id -> string residuo
    const [newClients, setNewClients] = useState([]); // rows yet to be saved

    // Inline create forms
    const [walletForm, setWalletForm] = useState(null); // null | {name, type, initial_balance}
    const [walletSaving, setWalletSaving] = useState(false);
    const [providerForm, setProviderForm] = useState(null); // null | {name, fido_limit, residuo}
    const [providerSaving, setProviderSaving] = useState(false);

    // P&L mese in corso (macro-rettifica)
    const [rettifica, setRettifica] = useState({ accrediti: "", uscite: "" });
    const [rettificaSaving, setRettificaSaving] = useState(false);
    const [rettificaLoaded, setRettificaLoaded] = useState(null); // { accrediti, uscite, occurred_at }

    // Auto-quadra apertura
    const [quadraOpen, setQuadraOpen] = useState(false);
    const [quadraAmount, setQuadraAmount] = useState("");
    const [quadraSaving, setQuadraSaving] = useState(false);

    const locked = agency?.apertura_locked === true;

    const load = async () => {
        if (!agencyId) return;
        setLoading(true);
        try {
            const [w, p, c, d, a, r] = await Promise.all([
                api.get("/wallets", { params: { agency_id: agencyId } }),
                api.get("/providers", { params: { agency_id: agencyId } }),
                api.get("/clients", { params: { agency_id: agencyId } }),
                api.get("/dashboard", { params: { agency_id: agencyId } }),
                api.get("/agencies"),
                api.get(`/agencies/${agencyId}/apertura/rettifica`).catch(() => ({ data: { accrediti: 0, uscite: 0 } })),
            ]);
            setWallets(w.data);
            setProviders(p.data);
            setClients(c.data);
            setFormula(d.data?.formula || null);
            setAgency(a.data.find((x) => x.id === agencyId) || null);
            setRettificaLoaded(r.data);
            setRettifica({
                accrediti: r.data?.accrediti ? String(r.data.accrediti) : "",
                uscite: r.data?.uscite ? String(r.data.uscite) : "",
            });
            const we = {};
            w.data.forEach((x) => (we[x.id] = String(x.balance ?? 0)));
            setWalletEdits(we);
            const pe = {};
            p.data.forEach((x) => {
                const residuo = (x.fido_limit || 0) - (x.outstanding_credit || 0);
                pe[x.id] = String(residuo);
            });
            setProviderEdits(pe);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const cashAndBankWallets = useMemo(
        () => wallets.filter((w) => ["cassa", "cassaforte", "banca"].includes(w.type)),
        [wallets],
    );
    const utiliWallets = useMemo(
        () => wallets.filter((w) => w.type === "utili_accantonati"),
        [wallets],
    );

    const saveWallet = async (w) => {
        const newBal = parseFloat(walletEdits[w.id]);
        if (isNaN(newBal)) {
            toast.error("Saldo non valido");
            return;
        }
        try {
            await api.patch(`/wallets/${w.id}`, { balance: newBal });
            toast.success(`${w.name}: saldo impostato a ${euro(newBal)}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const saveProvider = async (p) => {
        const residuo = parseFloat(providerEdits[p.id]);
        if (isNaN(residuo)) {
            toast.error("Residuo non valido");
            return;
        }
        if (residuo > p.fido_limit + 0.01) {
            toast.error(`Il residuo non può superare il fido di ${euro(p.fido_limit)}`);
            return;
        }
        const newOutstanding = p.fido_limit - residuo;
        try {
            await api.patch(`/providers/${p.id}`, {
                outstanding_credit: Math.max(0, newOutstanding),
            });
            toast.success(`${p.name}: residuo ${euro(residuo)}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const createWallet = async () => {
        if (!walletForm) return;
        if (!walletForm.name.trim()) {
            toast.error("Nome wallet obbligatorio");
            return;
        }
        const initial = parseFloat(walletForm.initial_balance) || 0;
        const currentRaw = String(walletForm.current_balance || "").trim();
        const overrideBalance = currentRaw !== "" ? parseFloat(currentRaw) : null;
        setWalletSaving(true);
        try {
            const { data: created } = await api.post("/wallets", {
                agency_id: agencyId,
                name: walletForm.name.trim(),
                type: walletForm.type,
                initial_balance: initial,
            });
            // If user specified a "saldo attuale" different from initial, patch balance only
            if (overrideBalance !== null && !isNaN(overrideBalance) && Math.abs(overrideBalance - initial) > 0.001) {
                await api.patch(`/wallets/${created.id}`, { balance: overrideBalance });
            }
            toast.success(`Wallet "${walletForm.name.trim()}" creato`);
            setWalletForm(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setWalletSaving(false);
        }
    };

    const createProvider = async () => {
        if (!providerForm) return;
        if (!providerForm.name.trim()) {
            toast.error("Nome fornitore obbligatorio");
            return;
        }
        const fido = parseFloat(providerForm.fido_limit) || 0;
        const residuo = providerForm.residuo === "" ? fido : (parseFloat(providerForm.residuo) || 0);
        if (residuo > fido + 0.01) {
            toast.error(`Il residuo non può superare il fido di ${euro(fido)}`);
            return;
        }
        setProviderSaving(true);
        try {
            const created = await api.post("/providers", {
                agency_id: agencyId,
                name: providerForm.name.trim(),
                fido_limit: fido,
            });
            // If user pre-set a residuo different from full fido, patch outstanding_credit
            const newOutstanding = fido - residuo;
            if (Math.abs(newOutstanding) > 0.01 && created?.data?.id) {
                await api.patch(`/providers/${created.data.id}`, {
                    outstanding_credit: Math.max(0, newOutstanding),
                });
            }
            toast.success(`Fornitore "${providerForm.name.trim()}" creato`);
            setProviderForm(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setProviderSaving(false);
        }
    };

    const saveRettifica = async () => {
        const accr = parseFloat(rettifica.accrediti) || 0;
        const usc = parseFloat(rettifica.uscite) || 0;
        if (accr < 0 || usc < 0) {
            toast.error("I valori non possono essere negativi");
            return;
        }
        setRettificaSaving(true);
        try {
            const r = await api.post(
                `/agencies/${agencyId}/apertura/rettifica`,
                { accrediti: accr, uscite: usc },
            );
            toast.success("P&L del mese in corso aggiornato", {
                description: `Accrediti ${euro(accr)} · Spese ${euro(usc)} · P&L ${euro(accr - usc)}`,
            });
            setRettificaLoaded(r.data);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setRettificaSaving(false);
        }
    };

    const addNewClientRow = () => {
        setNewClients([
            ...newClients,
            { key: Date.now() + Math.random(), name: "", phone: "", fido_limit: "", outstanding_debt: "" },
        ]);
    };

    const updateNewClient = (key, field, value) => {
        setNewClients((rows) =>
            rows.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
        );
    };

    const removeNewClient = (key) => {
        setNewClients((rows) => rows.filter((r) => r.key !== key));
    };

    const saveNewClient = async (row) => {
        if (!row.name.trim()) {
            toast.error("Nome cliente obbligatorio");
            return;
        }
        try {
            await api.post("/clients", {
                agency_id: agencyId,
                name: row.name.trim(),
                phone: row.phone.trim(),
                fido_limit: parseFloat(row.fido_limit) || 0,
                outstanding_debt: parseFloat(row.outstanding_debt) || 0,
                notes: "Apertura saldi",
            });
            toast.success(`${row.name} creato`);
            removeNewClient(row.key);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const [bulkSaving, setBulkSaving] = useState(false);

    const saveAllNewClients = async () => {
        const valid = newClients.filter((r) => r.name.trim());
        const skipped = newClients.length - valid.length;
        if (valid.length === 0) {
            toast.error("Compila almeno un nome cliente");
            return;
        }
        setBulkSaving(true);
        const savedKeys = [];
        const errors = [];
        // Salva in parallelo: ogni cliente è indipendente
        await Promise.all(
            valid.map(async (row) => {
                try {
                    await api.post("/clients", {
                        agency_id: agencyId,
                        name: row.name.trim(),
                        phone: row.phone.trim(),
                        fido_limit: parseFloat(row.fido_limit) || 0,
                        outstanding_debt: parseFloat(row.outstanding_debt) || 0,
                        notes: "Apertura saldi",
                    });
                    savedKeys.push(row.key);
                } catch (e) {
                    errors.push(`${row.name}: ${formatApiError(e.response?.data?.detail)}`);
                }
            }),
        );
        // Rimuovi solo le righe salvate con successo
        setNewClients((rows) => rows.filter((r) => !savedKeys.includes(r.key)));
        const parts = [];
        if (savedKeys.length > 0) parts.push(`${savedKeys.length} creati`);
        if (skipped > 0) parts.push(`${skipped} saltati (senza nome)`);
        if (errors.length > 0) parts.push(`${errors.length} con errori`);
        if (errors.length > 0) {
            toast.error(parts.join(" · "));
            errors.slice(0, 3).forEach((e) => toast.error(e));
        } else {
            toast.success(parts.join(" · ") || "Nessun cliente da salvare");
        }
        if (savedKeys.length > 0) await load();
        setBulkSaving(false);
    };

    const updateClientDebt = async (c, newDebt) => {
        try {
            await api.patch(`/clients/${c.id}`, { outstanding_debt: newDebt });
            toast.success(`${c.name}: debito ${euro(newDebt)}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (loading) return <div className="overline p-6">Caricamento…</div>;

    const scartoOk = formula ? Math.abs(formula.scarto || 0) < 0.01 : false;
    const scartoValue = formula ? formula.scarto || 0 : 0;
    const showQuadraButton = formula && Math.abs(scartoValue) >= 0.50;

    const utiliWalletExisting = wallets.find((w) => w.type === "utili_accantonati");

    const openQuadraModal = () => {
        // Default: assegna l'intero scarto (con segno invertito così la formula → 0).
        // Aumentando wallet.balance del wallet utili_accantonati di -scarto, gli
        // attivi crescono di -scarto e portano la formula a quadrare.
        const defaultRettifica = -scartoValue;
        setQuadraAmount(defaultRettifica.toFixed(2));
        setQuadraOpen(true);
    };

    const applyQuadra = async () => {
        const amt = parseFloat(quadraAmount);
        if (isNaN(amt) || Math.abs(amt) < 0.01) {
            toast.error("Importo non valido");
            return;
        }
        setQuadraSaving(true);
        try {
            // Patcha o crea wallet utili_accantonati. È l'unica via che incide
            // sulla formula senza ricreare flussi paralleli: utili_accantonati
            // contribuisce agli attivi e non ha contropartita nei passivi, quindi
            // +amt riduce |scarto| esattamente di |amt|.
            if (utiliWalletExisting) {
                const newBal = (utiliWalletExisting.balance || 0) + amt;
                await api.patch(`/wallets/${utiliWalletExisting.id}`, { balance: newBal });
            } else {
                await api.post("/wallets", {
                    agency_id: agencyId,
                    name: "Utili Accantonati",
                    type: "utili_accantonati",
                    initial_balance: amt,
                });
            }
            toast.success(`Utili accantonati: ${amt >= 0 ? "+" : ""}${euro(amt)}`);
            setQuadraOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setQuadraSaving(false);
        }
    };

    return (
        <div data-testid="apertura-tab" className="space-y-8">
            {isOnboardingFlow && !locked && (
                <div
                    className="bg-amber-50 border border-amber-300 p-5 flex items-start justify-between gap-4 max-w-4xl"
                    data-testid="apertura-onboarding-banner"
                >
                    <div className="flex items-start gap-3 flex-1">
                        <ChartLineUp size={22} weight="bold" className="mt-0.5 text-amber-700 flex-shrink-0" />
                        <div>
                            <div className="text-sm font-black tracking-tighter text-amber-900">
                                Imposta i saldi iniziali per partire dal punto giusto
                            </div>
                            <div className="text-xs text-amber-800 mt-1 leading-relaxed max-w-xl">
                                Sei in fase di onboarding. Configura cassa, fornitori,
                                sospesi clienti e P&amp;L del mese in corso adesso oppure
                                salta e fallo dopo da <strong>Impostazioni → Avanzate →
                                Apertura saldi</strong>. Senza apertura, lavorerai con
                                saldi a zero e potrai sempre tornarci.
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            toast.info("Apertura saltata", {
                                description: "Trovi questa pagina in Impostazioni → Avanzate",
                            });
                            navigate("/dashboard");
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-amber-400 text-amber-900 text-xs font-bold uppercase tracking-wider hover:bg-amber-100 flex-shrink-0"
                        data-testid="apertura-skip-btn"
                    >
                        <SkipForward size={14} weight="bold" />
                        Salta per ora
                    </button>
                </div>
            )}

            {locked && (
                <div
                    className="bg-zinc-900 text-white border-2 border-zinc-900 p-5 flex items-start justify-between gap-4 max-w-4xl"
                    data-testid="apertura-locked-banner"
                >
                    <div className="flex items-start gap-3">
                        <Lock size={22} weight="fill" className="mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="text-sm font-black tracking-tighter">
                                Apertura saldi bloccata
                            </div>
                            <div className="text-xs text-zinc-300 mt-1 leading-relaxed max-w-xl">
                                I dati di apertura sono protetti. Modificarli dopo la messa
                                in funzione quotidiana invalida la quadratura storica.
                                Sblocca solo se necessario e procedi con cautela.
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setPasswordModal("unlock")}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-white text-ink text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 flex-shrink-0"
                        data-testid="apertura-unlock-btn"
                    >
                        <LockOpen size={14} weight="bold" />
                        Sblocca modifica
                    </button>
                </div>
            )}

            {/* Intro + quadratura banner */}
            <div className="bg-white border border-zinc-200 p-6 max-w-4xl">
                <div className="overline mb-2 flex items-center gap-2">
                    <ChartLineUp size={12} weight="bold" /> Apertura saldi
                </div>
                <h2 className="text-2xl font-black tracking-tighter mb-3">
                    Importa la tua situazione attuale.
                </h2>
                <p className="text-sm text-zinc-600 leading-relaxed mb-4">
                    Non serve ricostruire mesi di storico. Imposta solo la{" "}
                    <strong>fotografia del momento</strong>: saldi di cassa, residuo
                    fido di ogni fornitore, sospesi dei tuoi clienti e utili già
                    accantonati. Da domani lavori normalmente e il P&L mensile parte
                    pulito.
                </p>
                {formula && (
                    <div
                        className={`p-3 border text-sm flex items-start gap-2 ${scartoOk ? "border-green-600 bg-green-50 text-green-900" : "border-amber-500 bg-amber-50 text-amber-900"}`}
                        data-testid="apertura-formula-banner"
                    >
                        {scartoOk ? (
                            <CheckCircle size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
                        ) : (
                            <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                            <strong>
                                Formula di quadratura: scarto di {euro(formula.scarto || 0)}
                            </strong>
                            <div className="text-xs mt-1 tabular-nums">
                                Attivi {euro(formula.attivi)} · Passivi {euro(formula.uscite)} · Cassa reale{" "}
                                {euro(formula.cassa_reale)}
                            </div>
                            {!scartoOk && (
                                <div className="text-xs mt-1">
                                    Continua a inserire i dati finché lo scarto non si avvicina a zero.
                                    Alcuni scarti minori sono fisiologici (arrotondamenti).
                                </div>
                            )}
                            {showQuadraButton && !locked && (
                                <button
                                    type="button"
                                    onClick={openQuadraModal}
                                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-xs font-bold uppercase tracking-wider hover:bg-zinc-800 transition-colors"
                                    data-testid="apertura-auto-quadra-btn"
                                >
                                    <Scales size={14} weight="bold" />
                                    Auto-quadra apertura
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 1. Fondi iniziali */}
            <fieldset
                disabled={locked}
                className={`space-y-8 ${locked ? "opacity-60" : ""}`}
                data-testid="apertura-editable-fieldset"
            >
            <section className="bg-white border border-zinc-200">
                <header className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
                    <WalletIcon size={14} weight="bold" />
                    <div>
                        <div className="text-sm font-black tracking-tighter">
                            1. Fondi iniziali
                        </div>
                        <div className="text-xs text-zinc-500">
                            Imposta il saldo corrente di cassa, cassaforte e banca al
                            momento del go-live.
                        </div>
                    </div>
                </header>
                {cashAndBankWallets.length === 0 ? (
                    <div className="p-6 text-sm text-zinc-500">
                        Nessun wallet cassa/banca configurato. Aggiungilo qui sotto.
                    </div>
                ) : (
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Wallet</th>
                                <th>Tipo</th>
                                <th className="text-right">Saldo attuale</th>
                                <th className="text-right">Nuovo saldo</th>
                                <th className="text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cashAndBankWallets.map((w) => (
                                <tr key={w.id} data-testid={`apertura-wallet-${w.id}`}>
                                    <td className="font-bold">{w.name}</td>
                                    <td className="text-xs uppercase text-zinc-500">
                                        {w.type.replace("_", " ")}
                                    </td>
                                    <td className="text-right tabular-nums text-zinc-500">
                                        {euro(w.balance)}
                                    </td>
                                    <td className="text-right" style={{ width: 180 }}>
                                        <MoneyInput
                                            className="inp tabular-nums text-right"
                                            value={walletEdits[w.id] || ""}
                                            onChange={(v) =>
                                                setWalletEdits({ ...walletEdits, [w.id]: v })
                                            }
                                            data-testid={`apertura-wallet-input-${w.id}`}
                                        />
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => saveWallet(w)}
                                            className="btn-secondary !py-2 !px-3 inline-flex items-center gap-2"
                                            data-testid={`apertura-wallet-save-${w.id}`}
                                        >
                                            <FloppyDisk size={12} weight="bold" />
                                            Salva
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {/* Inline create wallet */}
                {walletForm ? (
                    <div className="p-4 border-t border-zinc-200 bg-zinc-50" data-testid="apertura-wallet-form">
                        <div className="overline mb-3">Nuovo wallet</div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-4">
                                <label className="overline mb-1 block">Nome</label>
                                <input
                                    className="inp"
                                    autoFocus
                                    value={walletForm.name}
                                    onChange={(e) => setWalletForm({ ...walletForm, name: e.target.value })}
                                    placeholder="Es. Cassa Principale"
                                    data-testid="apertura-wallet-form-name"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="overline mb-1 block">Tipo</label>
                                <select
                                    className="inp"
                                    value={walletForm.type}
                                    onChange={(e) => setWalletForm({ ...walletForm, type: e.target.value })}
                                    data-testid="apertura-wallet-form-type"
                                >
                                    <option value="cassa">Cassa</option>
                                    <option value="cassaforte">Cassaforte</option>
                                    <option value="banca">Banca</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="overline mb-1 block" title="Capitale di apertura, immutabile dopo il go-live. Entra nella formula di quadratura.">
                                    Fondo cassa
                                </label>
                                <MoneyInput
                                    className="inp tabular-nums text-right"
                                    value={walletForm.initial_balance}
                                    onChange={(v) => setWalletForm({ ...walletForm, initial_balance: v })}
                                    placeholder="0,00"
                                    data-testid="apertura-wallet-form-balance"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="overline mb-1 block" title="Quanto c'è ORA fisicamente nel wallet. Lascia vuoto se uguale al fondo cassa.">
                                    Saldo attuale
                                </label>
                                <MoneyInput
                                    className="inp tabular-nums text-right"
                                    value={walletForm.current_balance || ""}
                                    onChange={(v) => setWalletForm({ ...walletForm, current_balance: v })}
                                    placeholder="opzionale"
                                    data-testid="apertura-wallet-form-current"
                                />
                            </div>
                            <div className="md:col-span-2 flex gap-2">
                                <button
                                    onClick={createWallet}
                                    disabled={walletSaving}
                                    className="btn-primary !py-2 !px-3 inline-flex items-center gap-2 flex-1 justify-center"
                                    data-testid="apertura-wallet-form-submit"
                                >
                                    <FloppyDisk size={12} weight="bold" />
                                    {walletSaving ? "…" : "Crea"}
                                </button>
                                <button
                                    onClick={() => setWalletForm(null)}
                                    className="btn-secondary !py-2 !px-3"
                                    aria-label="Annulla"
                                    data-testid="apertura-wallet-form-cancel"
                                >
                                    <Trash size={12} weight="bold" />
                                </button>
                            </div>
                        </div>
                        <div className="text-xs text-zinc-500 mt-3 leading-relaxed">
                            <strong>Fondo cassa</strong> = il capitale che hai messo in
                            apertura (es. 10.000 € posati nel cassetto un anno fa). Resta
                            fisso e fa parte degli attivi della formula di quadratura.
                            <br />
                            <strong>Saldo attuale</strong> = quanto c'è oggi nel cassetto
                            (es. 12.093 € perché nel frattempo si sono accumulati
                            utili/perdite). Lascialo vuoto se l'agenzia parte oggi da zero
                            e i due valori coincidono.
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() =>
                            setWalletForm({ name: "", type: "cassa", initial_balance: "", current_balance: "" })
                        }
                        className="w-full flex items-center justify-center gap-2 p-3 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-ink hover:bg-zinc-50 border-t border-zinc-200"
                        data-testid="apertura-wallet-add-btn"
                    >
                        <Plus size={14} weight="bold" /> Crea wallet
                    </button>
                )}
            </section>

            {/* 2. Residui fornitori */}
            <section className="bg-white border border-zinc-200">
                <header className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
                    <Handshake size={14} weight="bold" />
                    <div>
                        <div className="text-sm font-black tracking-tighter">
                            2. Residui fornitori
                        </div>
                        <div className="text-xs text-zinc-500">
                            Inserisci il residuo attuale che leggi sul portale di ogni
                            fornitore. L'esposizione (outstanding) viene calcolata come
                            fido − residuo.
                        </div>
                    </div>
                </header>
                {providers.length === 0 ? (
                    <div className="p-6 text-sm text-zinc-500">
                        Nessun fornitore configurato. Aggiungilo qui sotto.
                    </div>
                ) : (
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Fornitore</th>
                                <th className="text-right">Fido</th>
                                <th className="text-right">Residuo attuale</th>
                                <th className="text-right">Residuo dal portale</th>
                                <th className="text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.map((p) => {
                                const residuoDb = (p.fido_limit || 0) - (p.outstanding_credit || 0);
                                return (
                                    <tr key={p.id} data-testid={`apertura-provider-${p.id}`}>
                                        <td className="font-bold">{p.name}</td>
                                        <td className="text-right tabular-nums">{euro(p.fido_limit)}</td>
                                        <td className="text-right tabular-nums text-zinc-500">
                                            {euro(residuoDb)}
                                        </td>
                                        <td className="text-right" style={{ width: 180 }}>
                                            <MoneyInput
                                                className="inp tabular-nums text-right"
                                                value={providerEdits[p.id] || ""}
                                                onChange={(v) =>
                                                    setProviderEdits({ ...providerEdits, [p.id]: v })
                                                }
                                                data-testid={`apertura-provider-input-${p.id}`}
                                            />
                                        </td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => saveProvider(p)}
                                                className="btn-secondary !py-2 !px-3 inline-flex items-center gap-2"
                                                data-testid={`apertura-provider-save-${p.id}`}
                                            >
                                                <FloppyDisk size={12} weight="bold" />
                                                Salva
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {/* Inline create provider */}
                {providerForm ? (
                    <div className="p-4 border-t border-zinc-200 bg-zinc-50" data-testid="apertura-provider-form">
                        <div className="overline mb-3">Nuovo fornitore</div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-5">
                                <label className="overline mb-1 block">Nome</label>
                                <input
                                    className="inp"
                                    autoFocus
                                    value={providerForm.name}
                                    onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
                                    placeholder="Es. SNAI, Eurobet"
                                    data-testid="apertura-provider-form-name"
                                />
                            </div>
                            <div className="md:col-span-3">
                                <label className="overline mb-1 block">
                                    Fido <span className="text-zinc-400 normal-case">(0 = prepagato)</span>
                                </label>
                                <MoneyInput
                                    className="inp tabular-nums text-right"
                                    value={providerForm.fido_limit}
                                    onChange={(v) => setProviderForm({ ...providerForm, fido_limit: v })}
                                    placeholder="0,00"
                                    data-testid="apertura-provider-form-fido"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="overline mb-1 block">Residuo</label>
                                <MoneyInput
                                    className="inp tabular-nums text-right"
                                    value={providerForm.residuo}
                                    onChange={(v) => setProviderForm({ ...providerForm, residuo: v })}
                                    placeholder="= fido"
                                    data-testid="apertura-provider-form-residuo"
                                />
                            </div>
                            <div className="md:col-span-2 flex gap-2">
                                <button
                                    onClick={createProvider}
                                    disabled={providerSaving}
                                    className="btn-primary !py-2 !px-3 inline-flex items-center gap-2 flex-1 justify-center"
                                    data-testid="apertura-provider-form-submit"
                                >
                                    <FloppyDisk size={12} weight="bold" />
                                    {providerSaving ? "…" : "Crea"}
                                </button>
                                <button
                                    onClick={() => setProviderForm(null)}
                                    className="btn-secondary !py-2 !px-3"
                                    aria-label="Annulla"
                                    data-testid="apertura-provider-form-cancel"
                                >
                                    <Trash size={12} weight="bold" />
                                </button>
                            </div>
                        </div>
                        <div className="text-xs text-zinc-500 mt-2">
                            Lascia il <strong>Residuo</strong> vuoto per partire con fido pieno (outstanding 0).
                            Per i prepagati metti 0 nel fido e indica il saldo prepagato come residuo negativo dopo la creazione (oppure usa la pagina Fornitori).
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() =>
                            setProviderForm({ name: "", fido_limit: "", residuo: "" })
                        }
                        className="w-full flex items-center justify-center gap-2 p-3 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-ink hover:bg-zinc-50 border-t border-zinc-200"
                        data-testid="apertura-provider-add-btn"
                    >
                        <Plus size={14} weight="bold" /> Crea fornitore
                    </button>
                )}
            </section>

            {/* 3. Sospesi clienti aperti */}
            <section className="bg-white border border-zinc-200">
                <header className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
                    <Users size={14} weight="bold" />
                    <div>
                        <div className="text-sm font-black tracking-tighter">
                            3. Saldi clienti aperti
                        </div>
                        <div className="text-xs text-zinc-500">
                            Aggiungi i clienti con saldo già aperto: positivo se il cliente ha un sospeso (deve all'agenzia), negativo se è a credito (l'agenzia deve a lui — es. ha bonificato in banca senza giocare). Nessuna transazione storica viene creata.
                        </div>
                    </div>
                </header>

                {clients.length > 0 && (
                    <div className="px-6 py-3 border-b border-zinc-100">
                        <div className="overline mb-2">
                            Clienti esistenti ({clients.length})
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {clients.map((c) => (
                                <ClientDebtRow key={c.id} client={c} onSave={updateClientDebt} />
                            ))}
                        </div>
                    </div>
                )}

                <div className="p-6">
                    <div className="overline mb-3">Nuovi clienti da importare</div>
                    <div className="border border-zinc-200">
                        <table className="ft-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Telefono</th>
                                    <th className="text-right">Fido personale</th>
                                    <th className="text-right">Saldo iniziale</th>
                                    <th className="text-right">Azione</th>
                                </tr>
                            </thead>
                            <tbody>
                                {newClients.map((row) => (
                                    <tr key={row.key} data-testid={`apertura-newclient-row-${row.key}`}>
                                        <td>
                                            <input
                                                className="inp"
                                                value={row.name}
                                                onChange={(e) =>
                                                    updateNewClient(row.key, "name", e.target.value)
                                                }
                                                placeholder="Mario Rossi"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="inp"
                                                value={row.phone}
                                                onChange={(e) =>
                                                    updateNewClient(row.key, "phone", e.target.value)
                                                }
                                                placeholder="+39 333 1234567"
                                            />
                                        </td>
                                        <td className="text-right" style={{ width: 160 }}>
                                            <MoneyInput
                                                className="inp tabular-nums text-right"
                                                value={row.fido_limit}
                                                onChange={(v) =>
                                                    updateNewClient(row.key, "fido_limit", v)
                                                }
                                                placeholder="0,00"
                                            />
                                        </td>
                                        <td className="text-right" style={{ width: 160 }}>
                                            <MoneyInput
                                                className="inp tabular-nums text-right"
                                                value={row.outstanding_debt}
                                                onChange={(v) =>
                                                    updateNewClient(row.key, "outstanding_debt", v)
                                                }
                                                placeholder="0,00"
                                            />
                                        </td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => removeNewClient(row.key)}
                                                className="inline-flex items-center justify-center w-9 h-9 text-zinc-400 hover:text-red-600"
                                                aria-label="Rimuovi riga"
                                                data-testid={`apertura-newclient-remove-${row.key}`}
                                            >
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {newClients.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="text-center text-zinc-500 py-4 text-sm">
                                            Nessuna riga da importare. Clicca sotto per aggiungerne.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <button
                            onClick={addNewClientRow}
                            className="w-full flex items-center justify-center gap-2 p-3 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-ink hover:bg-zinc-50 border-t border-zinc-100"
                            data-testid="apertura-addclient-row"
                        >
                            <Plus size={14} weight="bold" /> Aggiungi cliente
                        </button>
                    </div>
                    {newClients.length > 0 && (
                        <div className="mt-3 flex justify-end">
                            <button
                                onClick={saveAllNewClients}
                                disabled={bulkSaving}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="apertura-newclient-save-all"
                            >
                                <FloppyDisk size={14} weight="bold" />
                                {bulkSaving
                                    ? "Salvataggio…"
                                    : `Salva tutti i clienti (${newClients.filter((r) => r.name.trim()).length})`}
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* 4. Utili accantonati */}
            <section className="bg-white border border-zinc-200">
                <header className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
                    <ChartLineUp size={14} weight="bold" />
                    <div>
                        <div className="text-sm font-black tracking-tighter">
                            4. Utili già accantonati
                        </div>
                        <div className="text-xs text-zinc-500">
                            Se in passato hai già messo da parte degli utili (cash non
                            ancora prelevato dai soci), imposta il saldo del wallet
                            "Utili Accantonati".
                        </div>
                    </div>
                </header>
                {utiliWallets.length === 0 ? (
                    <div className="p-6 text-sm text-zinc-500">
                        Non hai ancora un wallet di tipo "Utili Accantonati". Creane uno
                        da <strong>Wallet → Nuovo wallet → Tipo: Utili Accantonati</strong>,
                        poi torna qui per impostarne il saldo.
                    </div>
                ) : (
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Wallet</th>
                                <th className="text-right">Saldo attuale</th>
                                <th className="text-right">Nuovo saldo</th>
                                <th className="text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {utiliWallets.map((w) => (
                                <tr key={w.id} data-testid={`apertura-utili-${w.id}`}>
                                    <td className="font-bold">{w.name}</td>
                                    <td className="text-right tabular-nums text-zinc-500">
                                        {euro(w.balance)}
                                    </td>
                                    <td className="text-right" style={{ width: 180 }}>
                                        <MoneyInput
                                            className="inp tabular-nums text-right"
                                            value={walletEdits[w.id] || ""}
                                            onChange={(v) =>
                                                setWalletEdits({ ...walletEdits, [w.id]: v })
                                            }
                                            data-testid={`apertura-utili-input-${w.id}`}
                                        />
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => saveWallet(w)}
                                            className="btn-secondary !py-2 !px-3 inline-flex items-center gap-2"
                                            data-testid={`apertura-utili-save-${w.id}`}
                                        >
                                            <FloppyDisk size={12} weight="bold" />
                                            Salva
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* 5. P&L del mese in corso (macro-rettifica) */}
            <section className="bg-white border border-zinc-200" data-testid="apertura-rettifica-section">
                <header className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
                    <ChartLineUp size={14} weight="bold" />
                    <div>
                        <div className="text-sm font-black tracking-tighter">
                            5. P&amp;L del mese in corso
                        </div>
                        <div className="text-xs text-zinc-500">
                            Se stai facendo l'apertura a metà mese, inserisci qui il
                            totale di accrediti e spese già maturate dal 1° del mese a
                            oggi. Verranno registrati come due transazioni di rettifica
                            datate al 1° del mese, così la chiusura mensile rifletterà
                            il P&amp;L reale.
                        </div>
                    </div>
                </header>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <label className="overline mb-2 block">
                                Accrediti del mese
                            </label>
                            <MoneyInput
                                className="inp tabular-nums text-right"
                                value={rettifica.accrediti}
                                onChange={(v) => setRettifica({ ...rettifica, accrediti: v })}
                                placeholder="0,00"
                                data-testid="apertura-rettifica-accrediti"
                            />
                        </div>
                        <div>
                            <label className="overline mb-2 block">
                                Spese del mese
                            </label>
                            <MoneyInput
                                className="inp tabular-nums text-right"
                                value={rettifica.uscite}
                                onChange={(v) => setRettifica({ ...rettifica, uscite: v })}
                                placeholder="0,00"
                                data-testid="apertura-rettifica-uscite"
                            />
                        </div>
                        <div>
                            <label className="overline mb-2 block">
                                P&amp;L risultante
                            </label>
                            <div className="inp bg-zinc-50 tabular-nums text-right" data-testid="apertura-rettifica-pnl">
                                {euro(
                                    (parseFloat(rettifica.accrediti) || 0) -
                                    (parseFloat(rettifica.uscite) || 0)
                                )}
                            </div>
                        </div>
                    </div>
                    {rettificaLoaded && (rettificaLoaded.accrediti > 0 || rettificaLoaded.uscite > 0) && (
                        <div className="text-xs text-zinc-500 mb-3 p-2 bg-zinc-50 border border-zinc-100">
                            Rettifiche già salvate: accrediti {euro(rettificaLoaded.accrediti)} ·
                            spese {euro(rettificaLoaded.uscite)}. Salvando, le sostituisci.
                        </div>
                    )}
                    <button
                        onClick={saveRettifica}
                        disabled={rettificaSaving}
                        className="btn-secondary inline-flex items-center gap-2"
                        data-testid="apertura-rettifica-save"
                    >
                        <FloppyDisk size={12} weight="bold" />
                        {rettificaSaving ? "Salvataggio…" : "Salva P&L del mese"}
                    </button>
                    <div className="text-xs text-zinc-400 mt-3 leading-relaxed">
                        Nota tecnica: queste rettifiche entrano nel P&amp;L del mese ma
                        non muovono cassa né residui (la fotografia dei saldi sopra le
                        contiene già). Alla chiusura del mese verranno congelate come
                        le altre transazioni.
                    </div>
                </div>
            </section>
            </fieldset>

            {!locked && (
                <div className="bg-white border border-zinc-200 p-6 max-w-4xl flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                        <ShieldCheck size={22} weight="bold" className="mt-0.5 text-ink flex-shrink-0" />
                        <div>
                            <div className="text-sm font-black tracking-tighter">
                                Hai finito?
                            </div>
                            <div className="text-xs text-zinc-600 mt-1 max-w-xl leading-relaxed">
                                Quando la formula di quadratura ti soddisfa, conferma
                                l'apertura. Verrà richiesta la tua password. Da quel
                                momento in poi questa pagina sarà bloccata in sola
                                lettura — per proteggerti da modifiche accidentali che
                                falserebbero il bilancio.
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setPasswordModal("lock")}
                        className="btn-primary inline-flex items-center gap-2 flex-shrink-0"
                        data-testid="apertura-lock-btn"
                    >
                        <Lock size={14} weight="bold" />
                        Conferma e blocca
                    </button>
                </div>
            )}

            {passwordModal && (
                <AperturaPasswordModal
                    mode={passwordModal}
                    agencyId={agencyId}
                    onClose={() => setPasswordModal(null)}
                    onDone={() => {
                        setPasswordModal(null);
                        load();
                    }}
                />
            )}
            {quadraOpen && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setQuadraOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-lg bg-white border border-zinc-200 p-8"
                        data-testid="apertura-auto-quadra-modal"
                    >
                        <div className="overline mb-3 flex items-center gap-2">
                            <Scales size={12} weight="bold" /> Auto-quadra apertura
                        </div>
                        <h2 className="text-3xl font-black tracking-tighter mb-3">
                            Allinea lo scarto.
                        </h2>
                        <div className="text-sm text-zinc-600 mb-6">
                            Lo scarto residuo della formula è{" "}
                            <strong className="text-ink">{euro(scartoValue)}</strong>. Lo
                            registriamo come <strong>Utili Accantonati storici</strong> —
                            la differenza maturata in passato (utili non distribuiti o
                            perdite portate a nuovo). Per altri casi (cashback in banca,
                            POS pending, apporti soci recenti) usa una transazione
                            normale.
                        </div>

                        <label className="block mb-6">
                            <div className="overline mb-2">Importo da accantonare</div>
                            <MoneyInput
                                className="inp tabular-nums text-right"
                                value={quadraAmount}
                                onChange={setQuadraAmount}
                                placeholder="0,00"
                                data-testid="apertura-quadra-amount"
                            />
                            <div className="text-xs text-zinc-500 mt-1">
                                Predefinito: l'intero scarto ({euro(-scartoValue)}).
                                Modifica se vuoi rettificare solo una parte e gestire il
                                resto a mano (es. cashback, POS pending).
                            </div>
                        </label>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setQuadraOpen(false)}
                                className="btn-secondary flex-1"
                                data-testid="apertura-quadra-cancel"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={applyQuadra}
                                disabled={quadraSaving}
                                className="btn-primary flex-1"
                                data-testid="apertura-quadra-apply"
                            >
                                {quadraSaving ? "Applicazione…" : "Applica rettifica"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function AperturaPasswordModal({ mode, agencyId, onClose, onDone }) {
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const isLock = mode === "lock";

    const submit = async (e) => {
        e.preventDefault();
        if (!password) return;
        setSaving(true);
        try {
            await api.post(
                `/agencies/${agencyId}/apertura/${isLock ? "lock" : "unlock"}`,
                { password },
            );
            toast.success(
                isLock ? "Apertura bloccata" : "Apertura sbloccata, modifica con cautela",
            );
            onDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Errore");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="w-full max-w-md bg-white border-2 border-ink p-8"
                data-testid="apertura-password-modal"
            >
                <div className="overline mb-3 flex items-center gap-2">
                    {isLock ? (
                        <Lock size={12} weight="bold" />
                    ) : (
                        <LockOpen size={12} weight="bold" />
                    )}
                    {isLock ? "Conferma e blocca" : "Sblocca modifica"}
                </div>
                <h2 className="text-2xl font-black tracking-tighter mb-4">
                    {isLock ? "Blindatura apertura saldi" : "Sbloccare l'apertura?"}
                </h2>
                <div
                    className={`p-3 text-xs mb-5 leading-relaxed ${isLock ? "bg-zinc-50 border border-zinc-200 text-zinc-700" : "bg-red-50 border border-red-200 text-red-800"}`}
                >
                    {isLock ? (
                        <>
                            Confermando, i dati di apertura (fondi iniziali, residui
                            fornitori, sospesi clienti, utili, fondo cassa) verranno
                            protetti dalla modifica accidentale. Potrai sempre
                            sbloccarli da qui inserendo nuovamente la tua password.
                        </>
                    ) : (
                        <>
                            <strong>Attenzione.</strong> Modificare i saldi dopo la
                            messa in funzione quotidiana falserà la quadratura storica e
                            il P&L dei mesi già passati. Sblocca solo se devi correggere
                            un errore reale di apertura.
                        </>
                    )}
                </div>
                <label className="block mb-6">
                    <div className="overline mb-2">La tua password</div>
                    <input
                        type="password"
                        autoFocus
                        required
                        className="inp"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="apertura-password-input"
                    />
                </label>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary flex-1"
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        disabled={saving || !password}
                        className={`${isLock ? "btn-primary" : "btn-destructive"} flex-1`}
                        data-testid="apertura-password-submit"
                    >
                        {saving ? "…" : isLock ? "Blocca" : "Sblocca"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function ClientDebtRow({ client, onSave }) {
    const [val, setVal] = useState(String(client.outstanding_debt ?? 0));
    const changed = parseFloat(val) !== (client.outstanding_debt || 0);
    const debt = client.outstanding_debt || 0;
    const hint = debt < -0.01
        ? "a credito"
        : debt > 0.01
          ? "a debito"
          : "saldato";
    return (
        <div className="flex items-center gap-2 p-2 border border-zinc-100">
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{client.name}</div>
                <div className="text-xs text-zinc-500">
                    Fido {euro(client.fido_limit)} · {hint}
                </div>
            </div>
            <div style={{ width: 130 }}>
                <MoneyInput
                    className="inp tabular-nums text-right"
                    value={val}
                    onChange={setVal}
                    data-testid={`apertura-clientdebt-input-${client.id}`}
                />
            </div>
            <button
                onClick={() => onSave(client, parseFloat(val) || 0)}
                disabled={!changed}
                className="btn-secondary !py-2 !px-3 inline-flex items-center gap-1 disabled:opacity-30"
                data-testid={`apertura-clientdebt-save-${client.id}`}
                aria-label="Salva saldo"
            >
                <FloppyDisk size={12} weight="bold" />
            </button>
        </div>
    );
}
